import { getMockJobs } from "../data/mock-jobs";
import type { DataMode, Job, JobsApiResponse } from "../types/job";

const configuredBaseUrl = import.meta.env.VITE_JOB_LOBSTER_API_URL?.trim().replace(/\/$/, "");

export const AUTH_REQUIRED = "Enter your production operator token to connect to your private radar.";

function isJob(value: unknown): value is Job {
  if (!value || typeof value !== "object") return false;
  const job = value as Partial<Job>;
  return Boolean(
    typeof job.id === "string" &&
      typeof job.title === "string" &&
      typeof job.normalizedTitle === "string" &&
      typeof job.company === "string" &&
      job.location &&
      job.salary &&
      typeof job.applicationUrl === "string",
  );
}

export async function fetchJobs(signal?: AbortSignal): Promise<{ jobs: Job[]; mode: DataMode }> {
  if (!configuredBaseUrl) {
    await new Promise((resolve) => window.setTimeout(resolve, 280));
    return { jobs: getMockJobs(), mode: "mock" };
  }

  const jobs: Job[] = [];
  let offset: number | null = 0;
  while (offset !== null) {
    const response = await fetch(`${configuredBaseUrl}/api/v1/radar?offset=${offset}`, {
      signal,
      headers: { accept: "application/json", authorization: `Bearer ${sessionStorage.getItem("lobster-operator-token") ?? ""}` },
    });
    if (response.status === 401) throw new Error(AUTH_REQUIRED);
    if (!response.ok) throw new Error(`Job Lobster returned ${response.status}.`);
    const payload = (await response.json()) as Partial<JobsApiResponse> & { meta?: { nextOffset?: number | null } };
    if (!Array.isArray(payload.data) || !payload.data.every(isJob)) throw new Error("Job Lobster returned an incompatible API response.");
    jobs.push(...payload.data);
    const next: number | null = payload.meta?.nextOffset ?? null;
    if (next !== null && (!Number.isInteger(next) || next <= offset)) throw new Error("Job Lobster returned an invalid next page.");
    offset = next;
  }
  return { jobs, mode: "live" };
}

export function apiIsConfigured() {
  return Boolean(configuredBaseUrl);
}

export async function savePipeline(id: string, entry: import("./radar").Pipeline, token: string) {
  if (!configuredBaseUrl) throw new Error("Simulation mode: connect a local backend to save real applications.");
  const response = await fetch(`${configuredBaseUrl}/api/v1/pipeline/${encodeURIComponent(id)}`, {
    method: "PUT", headers: { "content-type": "application/json", authorization: `Bearer ${token}` }, body: JSON.stringify(entry),
  });
  if (!response.ok) { const payload = await response.json(); throw new Error(payload.error?.message ?? "Unable to save. Try again."); }
}
export async function fetchWatchlist(signal: AbortSignal): Promise<import("./radar").WatchCompany[]> {
  if (!configuredBaseUrl) throw new Error("Connect a local backend to inspect the employer watchlist.");
  const response = await fetch(`${configuredBaseUrl}/api/v1/watchlist`, { signal });
  if (!response.ok) throw new Error("Could not load the watchlist.");
  return (await response.json()).data;
}

export async function refreshRadar(offset: number, token: string) {
  if (!configuredBaseUrl) throw new Error("Connect a local backend before scanning.");
  const response = await fetch(`${configuredBaseUrl}/api/v1/radar/refresh?offset=${offset}`, { method: "POST", headers: { authorization: `Bearer ${token}` } });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error?.message ?? "Scan failed.");
  return payload as { nextOffset: number | null; data: { jobsAccepted: number; sourceFailures: number; status: string } };
}
